import { FeatureAccess, hasFeatureAccess } from '@/lib/subscription-feature-guards';
import { resolveEffectiveSubscriptionForUser } from '@/lib/subscription-consistency';
import type { JobRepository } from '../repository/job.repository';
import type { IUserRepository } from '../repository/interfaces';

function hasAdminOverride(userData: { roles?: string[]; role?: unknown } | null | undefined): boolean {
  const roles = userData?.roles ?? userData?.role;
  const roleList = Array.isArray(roles) ? roles : [roles];
  const normalizedRoles = roleList
    .filter((role): role is string => typeof role === 'string' && role.trim().length > 0)
    .map((role: string) => role.toUpperCase());
  return normalizedRoles.includes('ADMIN');
}

/**
 * Public job board postings (form-shaped documents in `jobs`).
 */
export interface JobPosting {
  id?: string;
  creatorId: string;
  creatorName: string;
  title: string;
  category: string;
  description: string;
  danceStyle: string;
  experienceLevel: string;
  ageGroup: string;
  imageUrl?: string;
  coverPhoto?: string;
  requirements: string[];
  equipmentProvided: string[];
  scheduleType: string;
  date: string;
  startTime: string;
  endTime: string;
  recurringDays: string[];
  duration: string;
  locationType: string;
  location: {
    address: string;
    city: string;
    state: string;
    zipCode: string;
    venue: string;
  };
  compensation: {
    type: string;
    minRate: number;
    maxRate: number;
    currency: string;
    paymentMethod: string;
    additionalBenefits: string[];
  };
  contactPreference: string;
  urgency: string;
  specialInstructions: string;
  status: 'open' | 'paused' | 'filled' | 'expired' | 'closed';
  applicants: number;
  views: number;
  createdAt: string;
  updatedAt: string;
}

function docTimeMs(value: unknown): number {
  if (value == null) return 0;
  if (typeof value === 'object' && value !== null && 'toMillis' in value) {
    const ms = (value as { toMillis: () => number }).toMillis();
    return typeof ms === 'number' ? ms : 0;
  }
  const d = new Date(value as string | number);
  const t = d.getTime();
  return Number.isFinite(t) ? t : 0;
}

function mapBoardRow(row: Record<string, unknown> & { id: string }): JobPosting {
  return { ...row } as unknown as JobPosting;
}

export class JobPostingsService {
  constructor(
    private readonly jobs: JobRepository,
    private readonly users: IUserRepository,
  ) {}

  async createJob(jobData: Omit<JobPosting, 'id'>): Promise<string> {
    const creatorRecord = await this.users.getUserById(jobData.creatorId);
    if (!creatorRecord) {
      throw new Error('Unable to verify user permissions for job creation');
    }

    const { tier: creatorTier } = await resolveEffectiveSubscriptionForUser(
      jobData.creatorId,
      creatorRecord,
      { syncUserDoc: true },
    );
    const canCreateJobs =
      hasAdminOverride(creatorRecord) ||
      hasFeatureAccess(creatorTier, FeatureAccess.CONTACT_BOOK_PROFESSIONALS);

    if (!canCreateJobs) {
      throw new Error('Royalty subscription or higher is required to create job postings');
    }

    return this.jobs.createJobBoardDocument({ ...jobData });
  }

  async getJob(jobId: string): Promise<JobPosting | null> {
    const row = await this.jobs.getJobBoardDocument(jobId);
    return row ? mapBoardRow(row) : null;
  }

  async getAllJobs(): Promise<JobPosting[]> {
    const rows = await this.jobs.listJobBoardDocumentsExcludingStatus('closed');
    rows.sort((a, b) => docTimeMs(b.createdAt) - docTimeMs(a.createdAt));
    return rows.map(mapBoardRow);
  }

  async getJobsByCreator(creatorId: string): Promise<JobPosting[]> {
    const rows = await this.jobs.listJobBoardDocumentsByCreator(creatorId);
    rows.sort((a, b) => docTimeMs(b.createdAt) - docTimeMs(a.createdAt));
    return rows.map(mapBoardRow);
  }

  async updateJob(jobId: string, updateData: Partial<JobPosting>): Promise<void> {
    await this.jobs.patchJobBoardDocument(jobId, { ...updateData });
  }

  async deleteJob(jobId: string): Promise<void> {
    await this.jobs.deleteJobBoardDocument(jobId);
  }

  async incrementApplicants(jobId: string, amount: number = 1): Promise<void> {
    await this.jobs.incrementJobBoardApplicants(jobId, amount);
  }

  async incrementViews(jobId: string, amount: number = 1): Promise<void> {
    await this.jobs.incrementJobBoardViews(jobId, amount);
  }
}
